import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, Loader2, CheckCircle, AlertTriangle, HelpCircle, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageFAQ = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchFAQ();
  }, [isSuperAdmin, navigate]);

  const fetchFAQ = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/faq`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch FAQ content');
      }
      
      const data = await response.json();
      setContent(data.content || '');
      setLastUpdated(data.updated_at);
    } catch (err) {
      console.error('Error fetching FAQ:', err);
      setError('Failed to load FAQ content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await fetch(`${API_URL}/api/faq`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save FAQ content');
      }
      
      const data = await response.json();
      setLastUpdated(data.updated_at);
      setSuccess('FAQ content saved successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError('Failed to save FAQ content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset FAQ to default content? This will overwrite your changes.')) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await fetch(`${API_URL}/api/faq/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to reset FAQ content');
      }
      
      const data = await response.json();
      setContent(data.content);
      setLastUpdated(data.updated_at);
      setSuccess('FAQ content reset to default!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting FAQ:', err);
      setError('Failed to reset FAQ content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Parse content into Q&A pairs for preview
  const parseFAQs = (text) => {
    if (!text) return [];
    
    const blocks = text.split(/\n\s*\n/);
    return blocks
      .filter(block => block.trim())
      .map(block => {
        const lines = block.trim().split('\n');
        const question = lines[0] || '';
        const answer = lines.slice(1).join('\n') || '';
        return { question, answer };
      })
      .filter(faq => faq.question && faq.answer);
  };

  const faqs = parseFAQs(content);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          <span className="ml-3 text-gray-600">Loading FAQ content...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <HelpCircle className="w-8 h-8 mr-3 text-yellow-500" />
              Manage FAQ
            </h1>
            <p className="text-gray-600 mt-2">
              Edit the Frequently Asked Questions displayed on the public FAQ page
            </p>
            {lastUpdated && (
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              {success}
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">Format Instructions</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Each FAQ has a <strong>Question</strong> on the first line</li>
              <li>• Followed by the <strong>Answer</strong> on the next line(s)</li>
              <li>• Separate each Q&A pair with a <strong>blank line</strong></li>
            </ul>
            <div className="mt-3 p-3 bg-white rounded border border-blue-100 font-mono text-xs text-gray-600">
              How does yiBooking work?<br/>
              yiBooking provides you with a customizable booking link...<br/>
              <br/>
              Is there a free plan?<br/>
              Yes! Our free plan includes up to 50 appointments...
            </div>
          </div>

          {/* Editor Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">FAQ Content</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {faqs.length} Q&A pair{faqs.length !== 1 ? 's' : ''} detected
                </span>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`flex items-center px-3 py-1 rounded-md text-sm transition-colors ${
                    showPreview 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            </div>
            
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-y"
              placeholder="How does yiBooking work?
yiBooking provides you with a customizable booking link...

Is there a free plan?
Yes! Our free plan includes up to 50 appointments..."
              data-testid="faq-content-textarea"
            />

            {/* Action Buttons */}
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={handleReset}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                data-testid="faq-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </button>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors disabled:opacity-50"
                data-testid="faq-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Preview</h2>
              {faqs.length === 0 ? (
                <p className="text-gray-500 italic">No valid Q&A pairs detected. Check your formatting.</p>
              ) : (
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{faq.question}</h3>
                      <p className="text-gray-600 whitespace-pre-wrap">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ManageFAQ;
